import BaseStore from '../BaseStore';

/**
 * A Store for local or session storage.
 */
class LocalSessionStore extends BaseStore {
  constructor(name, version, type) {
    super(name, version);
    this.prefix = 'AppService';
    this.store = type === 'local' ? window.localStorage : window.sessionStorage;
    this.reDateDetect = /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/;
  }

  prefixGenerator() {
    return `${this.prefix}_${this.version}_`;
  }

  keyGenerator(key) {
    return `${this.prefixGenerator()}${key}`;
  }

  parseWithDate(jsonString) {
    const resultObject = JSON.parse(jsonString, (_, value) => {
      if (typeof value === 'string' && this.reDateDetect.exec(value)) {
        return new Date(value);
      }
      return value;
    });
    return resultObject;
  }

  set(key, data, expiresAt) {
    if (data === null) {
      console.warn(
        'Storing `null` in local/session storage is an ambiguous operation. It will become impossible' +
          ' when fetching the key to determine if the returned value is null or if the key is not set. ' +
          'Value from the `get` call will returned as `undefined`. Consider deleting the entry all together.',
      );
    }
    this.store.setItem(this.keyGenerator(key), JSON.stringify({ data, expiresAt }));
    this.debugMsg('🖊️ added', key);
  }

  // This is a put operation so the entire object will be replaced.
  // That makes it the same as this.set
  update(key, data, expiresAt) {
    this.set(key, data, expiresAt);
    this.debugMsg('🖊️ updated', key);
  }

  get(key) {
    return new Promise((resolve, reject) => {
      try {
        const resolvedKey = this.keyGenerator(key);
        const rawData = this.store.getItem(resolvedKey);
        const parsedData = this.parseWithDate(rawData);
        this.debugMsg('🔎', resolvedKey, parsedData);
        resolve(parsedData === null ? undefined : parsedData);
      } catch (err) {
        reject(err);
      }
    });
  }

  async upsert(key, data, expiresAt) {
    const result = await this.get(key);
    if (result === undefined) {
      this.set(key, data, expiresAt);
    } else {
      this.update(key, data, expiresAt);
    }
  }

  delete(key) {
    return new Promise((resolve, reject) => {
      try {
        const resolvedKey = this.keyGenerator(key);
        this.store.removeItem(resolvedKey);
        this.debugMsg('❌ deleted', resolvedKey);
        resolve();
      } catch (err) {
        reject(err);
      }
    });
  }

  deleteStartsWith(substring) {
    return new Promise((resolve, reject) => {
      try {
        const resolvedSubstring = this.keyGenerator(substring);
        Object.keys({ ...this.store }).forEach((key) => {
          if (key.indexOf(resolvedSubstring) === 0) {
            this.store.removeItem(key);
          }
        });
        this.debugMsg('❌ deleted all records starting with', substring);
        resolve();
      } catch (err) {
        reject(err);
      }
    });
  }

  // Delete all entries with the prefix
  clear() {
    const prefix = this.prefixGenerator();
    Object.keys(this.store).forEach((k) => {
      if (k.indexOf(prefix) === 0) {
        this.delete(k);
      }
    });
    this.debugMsg('❌ All data deleted.');
  }
}

export default LocalSessionStore;
